import { updateFromServer, recordize, serializeEntity } from 'app/remote';
import { CancelToken } from 'app/utils/cancelToken';
import { EntityStringId } from 'app/types/entity';
import { warn } from 'app/services/logger';
import { getAllParticipants, isUpgradedToHost } from 'app/models/rtc/selectors';
import { JoinParams } from 'app/models/rtc/booking/constants';
import { AppDispatch, AppGetState } from 'redux';
import { postJoinRoom } from 'app/utils/api';
import { sessionRequest } from 'app/models/auth/actions';
import { JoinResultSupplementalData } from 'app/constants/schemas/rtc/JoinResult';
import { parseSupplementalData } from 'app/remote/response';
import { getEntityOfTypeOrThrow } from 'app/models/entities/selectors';
import { Participant, RecordizedJoinResult } from 'app/constants/schemas';

interface IJoinRoom {
  participant:    Participant;
  url:            string;
  token:          string;
  secret?:        string;
  upgradedToHost: boolean;
}
const joinRoom = ({ id, secret, joinParams, cancelToken }: {
  id: EntityStringId,
  secret?: string,
  joinParams?: JoinParams,
  cancelToken?: CancelToken,
}) => async (dispatch: AppDispatch, getState: AppGetState): Promise<IJoinRoom> => {
  // Get all paritcipants ahead of joining to be able to tell if
  // `host` appData changed
  const oldParticipants = getAllParticipants(getState());

  warn(`Getting join token for room ${id}`);
  const rawResponse: unknown = await dispatch(sessionRequest({
    ...postJoinRoom(id, { secret, join_params: joinParams }),
    cancelToken,
  }));

  const response: RecordizedJoinResult = recordize(rawResponse);
  const meta = parseSupplementalData(rawResponse, JoinResultSupplementalData);

  // Update only participant from the server response, rest of the
  // fields don't have ids so can't be stored
  dispatch(updateFromServer(serializeEntity(response.participant)));

  // Get a normalized entity for the participant because
  // response.participant is a recordized (deeply-nested) variant
  // that we don't want to use in the app if possible
  const participantId = response.participant.id;
  const participant = getEntityOfTypeOrThrow(getState(), Participant, participantId);

  const oldParticipant = oldParticipants?.get(participantId);
  const upgradedToHost = isUpgradedToHost(oldParticipant, participant);

  return {
    participant,
    upgradedToHost,
    token:  response.token,
    url:    response.server.socketUrl,
    secret: meta?.[participant.id].secret,
  };
};

export default joinRoom;
